import React, { type ReactNode, createContext, useContext } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { NOOP } from '~/utils/noops';
import { stripIndents } from '~/utils/stripIndent';

export function renderPrompt(children: ReactNode) {
  const html = renderToStaticMarkup(children);

  return html
    .replace(/&(amp;)*lt;/g, '<')
    .replace(/&(amp;)*gt;/g, '>')
    .replace(/&(amp;)*amp;/g, '&')
    .replace(/&(amp;)*quot;/g, '"')
    .replace(/&(amp;)*#x27;/g, "'")
    .replace(/&(amp;)*#x2F;/g, '/')
    .replace(/&(amp;)*#x60;/g, '`')
    .trim();
}

export const BoltArtifact = () => '<boltArtifact>';

export const BoltAction = () => '<boltAction>';

export const CACHE_MARKER = '<cache_point />';

export const CachePoint = () => CACHE_MARKER;

// alias for improved clarity
export const textBlock = stripIndents;

export const Line: React.FC<{ children: ReactNode }> = ({ children }) => {
  return children;
};

export const Newline: React.FC<{ count?: number }> = ({ count = 1 }) => '\n'.repeat(count);

export const Examples: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Section name="examples" spaceBetween={1}>
      {children}
    </Section>
  );
};

export const Section: React.FC<{
  name: string;
  children: ReactNode;
  spaceBetween?: boolean | number;
  attributes?: Record<string, string | number | undefined>;
}> = ({ name, spaceBetween = 2, children, attributes = {} }) => {
  const computedAttributes = Object.entries(attributes)
    .filter(([_, value]) => value != null)
    .map(([key, value]) => `${key}=${JSON.stringify(value?.toString())}`);

  return (
    <>
      {`<${name}${computedAttributes.length > 0 ? ` ${computedAttributes.join(' ')}` : ''}>`}
      <Newline />
      <Container spaceBetween={spaceBetween}>{children}</Container>
      <Newline />
      {`</${name}>`}
    </>
  );
};

export const Indent: React.FC<{ children: ReactNode; spaces?: number }> = ({ children, spaces = 2 }) => {
  return renderToStaticMarkup(children)
    .split('\n')
    .map((line) => (line ? ' '.repeat(spaces) + line : line))
    .join('\n');
};

export const CurrentYear: React.FC = () => {
  return `The year is ${new Date().getFullYear()}.`;
};

export const Container: React.FC<{ spaceBetween?: boolean | number; children: ReactNode }> = ({
  spaceBetween = 1,
  children,
}) => {
  const items = flattenChildren(children);

  if (items.length === 0) {
    return null;
  }

  const newlineCount = typeof spaceBetween === 'number' ? spaceBetween : spaceBetween ? 2 : 0;
  const separator = '\n'.repeat(newlineCount);

  return items.map((child, index) => (
    <React.Fragment key={index}>
      {child}
      {items.length > 1 && index < items.length - 1 && separator}
    </React.Fragment>
  ));
};

interface ListProps {
  type?: 'ordered' | 'unordered';
  symbol?: string;
  spaceBetween?: boolean | number;
  indent?: true;
  spaces?: number;
  children: ReactNode;
}

interface ListContextValue {
  type: 'ordered' | 'unordered';
  symbol?: string;
  index?: number;
}

const ListContext = createContext<ListContextValue | null>(null);

export const UnorderedList: React.FC<Omit<ListProps, 'type'>> = ({ children, ...props }) => (
  <List type="unordered" {...props}>
    {children}
  </List>
);

export const OrderedList: React.FC<Omit<ListProps, 'type'>> = ({ children, ...props }) => (
  <List type="ordered" {...props}>
    {children}
  </List>
);

const List: React.FC<ListProps> = (props) => {
  const { type = 'ordered', spaceBetween = 1, symbol, children } = props;

  const items = flattenChildren(children);

  const listContent = (
    <Container spaceBetween={spaceBetween}>
      {items.map((child, index) => (
        <ListContext.Provider
          key={`list-item-${index}`}
          value={{
            type,
            symbol,
            index,
          }}
        >
          {child}
        </ListContext.Provider>
      ))}
    </Container>
  );

  return props.indent ? <Indent spaces={props.spaces}>{listContent}</Indent> : listContent;
};

export const ListItem: React.FC<{
  inline?: true;
  spaceBetween?: boolean | number;
  symbol?: string;
  children: ReactNode;
}> = ({ inline, spaceBetween = 1, symbol, children }) => {
  const context = useContext(ListContext);

  if (!context) {
    return <>{children}</>;
  }

  const { type, symbol: contextSymbol, index = 0 } = context;

  const prefix = symbol ?? contextSymbol ?? (type === 'ordered' ? `${index + 1}.` : '-');

  const items = React.Children.toArray(children);

  return (
    <Container spaceBetween={inline ? false : spaceBetween}>
      <>
        {prefix} {items[0]}
      </>
      {items.slice(1)}
    </Container>
  );
};

interface ConditionalGroupProps {
  render?: boolean;
  children: ReactNode;
}

// this can be a noop because this component will never be rendered and its children will be flattend
export const ConditionalGroup: React.FC<ConditionalGroupProps> = NOOP;

function flattenChildren(children: ReactNode) {
  return React.Children.toArray(children).flatMap((child) => {
    if (typeof child === 'object' && 'type' in child && child.type === ConditionalGroup) {
      const props = child.props as ConditionalGroupProps;

      if (!props.render) {
        return [];
      }

      return React.Children.toArray((child.props as ConditionalGroupProps).children);
    } else if (!child || typeof child !== 'string' || child !== '') {
      return child;
    } else {
      return [];
    }
  });
}
